import type { Filter } from '@gravitate-js/excalibrr/dist/components/Grid/SearchGridHeader/DynamicFilterForm/types'
import type { DateFilters } from '@/hooks/useDateRangeFilter'
import type {
  DeliveryReconOverviewResponse,
  DeliveryReconOverviewRow,
} from '@/pages/DeliveryReconciliation/DeliveryReport/types'
import type {
  ConfigData,
  DeliveryReconSiteOverviewResponse,
  DeliveryReconSiteOverviewRow,
} from '@/pages/DeliveryReconciliation/DeliveryConfig/types'

import { useApi } from '@gravitate-js/the-armory'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'
import { isCustomConfig } from '@/pages/DeliveryReconciliation/DeliveryConfig/util'
import type { InferResponse } from '@/hooks/useApiClient'
import { useMemo, useState } from 'react'

const BASE_OVERVIEW_KEY = 'useDeliveryReconOverview'

export const useDeliveryRecon = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()

  const [overviewKey, setOverviewKey] = useState([BASE_OVERVIEW_KEY])

  const useDeliveryReconOverview = (dateFilters: DateFilters, filters: Filter) => {
    const filter = useMemo(
      () => ({
        from_date: dateFilters.From,
        to_date: dateFilters.To,
        ...filters,
      }),
      [dateFilters.From, dateFilters.To, filters]
    )

    return useQuery<DeliveryReconOverviewResponse>({
      queryKey: [BASE_OVERVIEW_KEY, JSON.stringify(filter)],
      queryFn: () => {
        setOverviewKey([BASE_OVERVIEW_KEY, JSON.stringify(filter)])
        return api.post('delivery_reconciliation/overview', { filter })
      },
      enabled: Boolean(filter.from_date && filter.to_date),
    })
  }

  const useApprove = useMutation({
    mutationFn: (selected: DeliveryReconOverviewRow[]) =>
      api.post('delivery_reconciliation/bulk/approve', { match_ids: selected.map((s) => s.match_id) }),
    onMutate: async (selected) => {
      await queryClient.cancelQueries({ queryKey: overviewKey })
      const previousData = queryClient.getQueryData<DeliveryReconOverviewResponse>(overviewKey)
      if (previousData?.rows?.length) {
        const updatedRows = previousData.rows.map((row) =>
          selected.find((s) => s.match_id === row.match_id) ? { ...row, status: 'approved' } : row
        )
        queryClient.setQueryData(overviewKey, { ...previousData, rows: updatedRows })
      }

      return { previousData }
    },
    onSuccess: (data: InferResponse<'/delivery_reconciliation/bulk/approve'>) => {
      const previousData = queryClient.getQueryData<DeliveryReconOverviewResponse>(overviewKey)
      const approved: DeliveryReconOverviewRow[] = data ?? []
      if (previousData?.rows && approved?.length) {
        const updatedRows = previousData.rows.map((row) => {
          const updatedRow = approved.find((u) => u.match_id === row.match_id)
          return updatedRow ? updatedRow : row
        })
        queryClient.setQueryData(overviewKey, {
          ...previousData,
          rows: updatedRows,
        })
      }
    },
    onError: (_err, _id, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(overviewKey, context.previousData)
      }
    },
  })

  const useUnapprove = useMutation({
    mutationFn: (selected: DeliveryReconOverviewRow[]) =>
      api.post('delivery_reconciliation/bulk/unapprove', { match_ids: selected.map((s) => s.match_id) }),
    onMutate: async (selected) => {
      await queryClient.cancelQueries({ queryKey: overviewKey })
      const previousData = queryClient.getQueryData<DeliveryReconOverviewResponse>(overviewKey)
      if (previousData?.rows?.length) {
        const updatedRows = previousData.rows.map((row) =>
          selected.find((s) => s.match_id === row.match_id)
            ? {
                ...row,
                status: row.details.some((d) => d.type === 'bol')
                  ? 'matched'
                  : row.details.some((d) => d.type === 'delivery')
                    ? 'approved'
                    : 'unmatched',
              }
            : row
        )
        queryClient.setQueryData(overviewKey, { ...previousData, rows: updatedRows })
      }

      return { previousData }
    },
    onSuccess: (data: InferResponse<'/delivery_reconciliation/bulk/unapprove'>) => {
      const previousData = queryClient.getQueryData<DeliveryReconOverviewResponse>(overviewKey)
      const unapproved: DeliveryReconOverviewRow[] = data ?? []
      if (previousData?.rows && unapproved?.length) {
        const updatedRows = previousData.rows.map((row) => {
          const updatedRow = unapproved.find((u) => u.match_id === row.match_id)
          return updatedRow ? updatedRow : row
        })
        queryClient.setQueryData(overviewKey, {
          ...previousData,
          rows: updatedRows,
        })
      }
    },
  })

  const useMatch = useMutation({
    mutationFn: (selected: DeliveryReconOverviewRow[]) =>
      api.post('delivery_reconciliation/bulk/match', { match_ids: selected.map((s) => s.match_id) }),
    onMutate: async (selected) => {
      await queryClient.cancelQueries({ queryKey: overviewKey })
      const previousData = queryClient.getQueryData<DeliveryReconOverviewResponse>(overviewKey)
      const originalMatchIds = new Set(selected.map((s) => s.match_id))

      return { previousData, originalMatchIds }
    },
    onSuccess: (data, _, context) => {
      const previousData = queryClient.getQueryData<DeliveryReconOverviewResponse>(overviewKey)
      const serverMatches: DeliveryReconOverviewRow[] = data?.[0] ?? []

      if (previousData?.rows && serverMatches?.length && context?.originalMatchIds) {
        const filteredExisting = previousData.rows.filter((row) => !context.originalMatchIds.has(row.match_id))
        const updatedRows = [...serverMatches, ...filteredExisting]

        queryClient.setQueryData(overviewKey, {
          ...previousData,
          rows: updatedRows,
        })
      }
    },
  })

  const useUnmatch = useMutation({
    mutationFn: (selected: DeliveryReconOverviewRow[]) =>
      api.post('delivery_reconciliation/bulk/unmatch', { match_ids: selected.map((s) => s.match_id) }),
    onMutate: async (selected) => {
      await queryClient.cancelQueries({ queryKey: overviewKey })
      const previousData = queryClient.getQueryData<DeliveryReconOverviewResponse>(overviewKey)
      const originalMatchIds = new Set(selected.map((s) => s.match_id))
      return { previousData, originalMatchIds }
    },
    onSuccess: (data: InferResponse<'/delivery_reconciliation/bulk/unmatch'>, _, context) => {
      const previousData = queryClient.getQueryData<DeliveryReconOverviewResponse>(overviewKey)
      const unmatched: DeliveryReconOverviewRow[] = data ?? []

      if (previousData?.rows && unmatched?.length && context?.originalMatchIds) {
        const filteredExisting = previousData.rows.filter((row) => !context.originalMatchIds.has(row.match_id))
        const updatedRows = [...unmatched, ...filteredExisting]

        queryClient.setQueryData(overviewKey, {
          ...previousData,
          rows: updatedRows,
        })
      }
    },
  })

  const useVoid = useMutation({
    mutationFn: (selected: DeliveryReconOverviewRow[]) =>
      api.post('delivery_reconciliation/bulk/void', { match_ids: selected.map((s) => s.match_id) }),
    onMutate: async (selected) => {
      await queryClient.cancelQueries({ queryKey: overviewKey })
      const previousData = queryClient.getQueryData<DeliveryReconOverviewResponse>(overviewKey)
      if (previousData?.rows?.length) {
        const updatedRows = previousData.rows.map((row) =>
          selected.find((s) => s.match_id === row.match_id) ? { ...row, status: 'voided' } : row
        )
        queryClient.setQueryData(overviewKey, { ...previousData, rows: updatedRows })
      }

      return { previousData }
    },
    onSuccess: (data: InferResponse<'/delivery_reconciliation/bulk/void'>) => {
      const previousData = queryClient.getQueryData<DeliveryReconOverviewResponse>(overviewKey)
      const voided: DeliveryReconOverviewRow[] = data ?? []
      if (previousData?.rows && voided?.length) {
        const updatedRows = previousData.rows.map((row) => {
          const updatedRow = voided.find((u) => u.match_id === row.match_id)
          return updatedRow ? updatedRow : row
        })
        queryClient.setQueryData(overviewKey, {
          ...previousData,
          rows: updatedRows,
        })
      }
    },
  })

  const useUnlink = useMutation({
    mutationFn: ({
      selected,
    }: { id: DeliveryReconOverviewRow['match_id']; selected: DeliveryReconOverviewRow['details'][number] }) => {
      const delivery_id = selected.source_identifier?.[0]?.id
      return api.post(
        'delivery_reconciliation/unlink',
        selected.type === 'delivery'
          ? {
              delivery_id,
            }
          : {
              order_id: selected.order_id,
            }
      )
    },
    onMutate: async ({ id, selected }) => {
      await queryClient.cancelQueries({ queryKey: overviewKey })
      const previousData = queryClient.getQueryData<DeliveryReconOverviewResponse>(overviewKey)
      if (previousData?.rows?.length) {
        const updatedRows = previousData.rows.map((row) => {
          if (row.match_id === id) {
            const updatedDetails = row.details.filter((d) => d.id !== selected.id)
            return { ...row, details: updatedDetails }
          }
          return row
        })
        queryClient.setQueryData(overviewKey, { ...previousData, rows: updatedRows })
      }

      return { previousData }
    },
    onSuccess: (data: InferResponse<'/delivery_reconciliation/unlink'>, { id }) => {
      const previousData = queryClient.getQueryData<DeliveryReconOverviewResponse>(overviewKey)
      const updatedRows: DeliveryReconOverviewRow[] = data ?? []

      if (previousData?.rows && updatedRows.length > 0) {
        const updatedRowsMap = new Map(updatedRows.map((row) => [row.match_id, row]))
        const existingRowIds = new Set(previousData.rows.map((row) => row.match_id))
        const newRows = previousData.rows.map((row) => {
          if (row.match_id === id || updatedRowsMap.has(row.match_id)) {
            return updatedRowsMap.get(row.match_id) || row
          }
          return row
        })

        const brandNewRows = updatedRows.filter((row) => !existingRowIds.has(row.match_id))
        queryClient.setQueryData(overviewKey, {
          ...previousData,
          rows: [...newRows, ...brandNewRows],
        })
      }
    },
  })

  const useSiteOverview = () =>
    useQuery({
      queryKey: ['siteOverviewQuery'],
      queryFn: () => api.post('delivery_reconciliation/site_overview', {}),
    })

  // store level config
  const useSetThresholds = useMutation({
    mutationFn: (payload: DeliveryReconSiteOverviewRow) => api.post('delivery_reconciliation/set_thresholds', payload),
    onSuccess: (data: DeliveryReconSiteOverviewRow) => {
      queryClient
        .getQueriesData<DeliveryReconSiteOverviewResponse>({ queryKey: ['siteOverviewQuery'] })
        .map(([queryKey, prevData]) => {
          if (prevData?.length) {
            const updatedRows = prevData.map((row: DeliveryReconSiteOverviewRow) =>
              row.store_id === data.store_id && row.tank_id === data.tank_id ? { ...row, ...data } : row
            )
            queryClient.setQueryData(queryKey, updatedRows)
          }
        })
    },
  })

  // global level config
  const useSetConfig = useMutation({
    mutationFn: (payload: Partial<DeliveryReconSiteOverviewRow>) =>
      api.post('delivery_reconciliation/set_config', {
        approve_hour_threshold: payload.delivery_approve_hour_threshold,
        approve_volume_threshold: payload.delivery_approve_volume_threshold,
        link_hour_threshold: payload.delivery_match_hour_threshold,
        link_volume_threshold: payload.delivery_match_volume_threshold,
      }),
    onMutate: async (newConfig) => {
      await queryClient.cancelQueries({ queryKey: ['deliveryReconConfig'] })

      const previousConfig = queryClient.getQueryData<ConfigData>(['deliveryReconConfig'])

      // Update global config
      queryClient.setQueryData(['deliveryReconConfig'], {
        approve_hour_threshold: newConfig.delivery_approve_hour_threshold!,
        approve_volume_threshold: newConfig.delivery_approve_volume_threshold!,
        link_hour_threshold: newConfig.delivery_match_hour_threshold!,
        link_volume_threshold: newConfig.delivery_match_volume_threshold!,
      })

      // Update site overview queries
      const snapshots = queryClient
        .getQueriesData<DeliveryReconSiteOverviewResponse>({ queryKey: ['siteOverviewQuery'] })
        .map(([queryKey, data]) => {
          const prevData = data
          if (!data?.length) return { queryKey, prevData }

          const updatedRows = data.map((row: DeliveryReconSiteOverviewRow) => {
            const updateMatch = !isCustomConfig(row, 'match', previousConfig)
            const updateApprove = !isCustomConfig(row, 'approve', previousConfig)

            return {
              ...row,
              ...(updateMatch && {
                delivery_match_hour_threshold: newConfig.delivery_match_hour_threshold,
                delivery_match_volume_threshold: newConfig.delivery_match_volume_threshold,
              }),
              ...(updateApprove && {
                delivery_approve_hour_threshold: newConfig.delivery_approve_hour_threshold,
                delivery_approve_volume_threshold: newConfig.delivery_approve_volume_threshold,
              }),
            }
          })

          queryClient.setQueryData(queryKey, updatedRows)
          return { queryKey, prevData }
        })

      return { previousConfig, snapshots }
    },

    onError: (_err, _newConfig, context) => {
      queryClient.setQueryData(['deliveryReconConfig'], context?.previousConfig)
      context?.snapshots?.forEach(({ queryKey, prevData }) => {
        queryClient.setQueryData(queryKey, prevData)
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['siteOverviewQuery'] })
      queryClient.invalidateQueries({ queryKey: ['deliveryReconConfig'] })
    },
  })

  const useGetConfig = () => {
    return useQuery({
      queryKey: ['deliveryReconConfig'],
      queryFn: () => api.post('delivery_reconciliation/get_config'),
    })
  }

  const useGetHistory = (match_id: string | null) => {
    return useQuery({
      queryKey: ['deliveryReconHistory', match_id],
      queryFn: () => api.post('delivery_reconciliation/history', { match_id }),
      enabled: Boolean(match_id),
    })
  }

  return {
    useDeliveryReconOverview,
    useApprove,
    useUnapprove,
    useMatch,
    useUnmatch,
    useVoid,
    useUnlink,
    useSiteOverview,
    useSetThresholds,
    useSetConfig,
    useGetConfig,
    useGetHistory,
  }
}
